import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  EditFormStoreEvent,
  EditFormStoreEventKind,
  EditFormStoreRowClickEvent,
  isEditFormStoreRowClickEvent,
  isEditFormStoreSetupButtonClickEvent,
} from 'config/pbEvents';
import { FieldType } from 'entities/common';
import { FieldDefaultValueKind } from 'entities/common/fieldDefaultValue';
import { ToggleStateStore, Unsubscribe } from 'stores/common';
import { ILocalStore, IServerEntity } from 'stores/interfaces';
import { EditFormStoreMediator } from 'stores/local/forms/EditFormStore/EditFormStoreMediator';
import { TableRowEntityType } from 'stores/local/forms/types';
import { NewFormulaModel } from 'stores/models/NewFormulaModel';

import { FieldSettingsStoreCommonParams } from '../types';

type PrivateFields = '_currentEntity' | '_setCurrentEntity';

/** Стор для построения формулы*/
class FormulaSettingsStore implements ILocalStore, IServerEntity<string> {
  constructor({
    mediator,
    getTableRowEntityByCatalogId,
  }: FieldSettingsStoreCommonParams) {
    makeObservable<FormulaSettingsStore, PrivateFields>(this, {
      _currentEntity: observable.ref,
      currentEntity: computed,
      _setCurrentEntity: action.bound,
    });

    this._mediator = mediator;

    this._subscribeToSetupButtonClickEvent =
      this._subscribeToSetupButtonClickEvent.bind(this);
    this._handleEventRowClickEvent = this._handleEventRowClickEvent.bind(this);

    this._storeInitSubscriber = this._mediator.subscribeToSetupButtonClickEvent(
      this._subscribeToSetupButtonClickEvent
    );
    this._controllerReaction = reaction(
      () => this.drawerController.opened,
      (opened) => {
        if (!opened) {
          this._resetRowSelectSubscription();
          this._mediator.emitToggleSettingsDrawerEvent({ payload: opened });
          this.newFormulaModel.reset();

          return;
        }

        this._mediator.emitSetHighlightedRowsEvent({
          rows: this.newFormulaModel.usedFormFieldIds,
        });
      }
    );

    this.newFormulaModel = new NewFormulaModel({
      getLabelByFormFieldEntityId: (id) =>
        getTableRowEntityByCatalogId(id)?.catalogField.label || '',
    });
  }

  readonly newFormulaModel: NewFormulaModel;
  readonly drawerController = new ToggleStateStore();

  private readonly _mediator: EditFormStoreMediator;

  private _reaction: IReactionDisposer | null = null;

  private _rowSelectSubscriber: Unsubscribe | null = null;
  private readonly _storeInitSubscriber: Unsubscribe;
  private readonly _controllerReaction: IReactionDisposer;

  private _currentEntity: TableRowEntityType | null = null;

  /** Формула, преобразованная под формат бэка */
  get toServerFormat(): string {
    return this.newFormulaModel.toServerFormat;
  }

  get currentEntity() {
    return this._currentEntity;
  }

  private _setCurrentEntity(entity: TableRowEntityType): void {
    this._currentEntity = entity;

    this.newFormulaModel.fromServerFormat(
      entity.formField.defaultValue?.value || ''
    );
  }

  private _handleEventRowClickEvent(event: EditFormStoreRowClickEvent): void {
    this.newFormulaModel.addEntity(event.payload.formField);
  }

  private _subscribeToRowSelectEvent(event: EditFormStoreEvent): void {
    if (isEditFormStoreRowClickEvent(event)) {
      this._rowSelectSubscriber = this._mediator.subscribeToRowClickEvent(
        this._handleEventRowClickEvent
      );
      this._reaction = reaction(
        () => this.newFormulaModel.usedFormFieldIds,
        (keys) => {
          this._mediator.emitSetHighlightedRowsEvent({ rows: keys });
        }
      );
    }
  }

  private _subscribeToSetupButtonClickEvent(event: EditFormStoreEvent): void {
    if (
      isEditFormStoreSetupButtonClickEvent(event) &&
      event.payload.catalogField.dataType === FieldType.formula
    ) {
      this.drawerController.open();
      this._setCurrentEntity(event.payload);
      this._subscribeToRowSelectEvent({
        kind: EditFormStoreEventKind.ROW_CLICK,
        payload: event.payload,
      });
    }
  }

  private _resetHighLightedRows(): void {
    this._mediator.emitSetHighlightedRowsEvent({ rows: [] });
  }

  private _resetRowSelectSubscription(): void {
    this._rowSelectSubscriber?.();
    this._reaction?.();
    this._reaction = null;
    this._resetHighLightedRows();
  }

  handleSave(): void {
    this._currentEntity?.formField.setDefaultValue({
      kind: FieldDefaultValueKind.formula,
      value: this.toServerFormat,
    });
    this.drawerController.close();
  }

  destroy(): void {
    this._resetRowSelectSubscription();
    this._storeInitSubscriber();
    this._controllerReaction();

    this.newFormulaModel.destroy();
  }
}

export default FormulaSettingsStore;
